// src/utils/timezoneUtilities.js

// Company timezone where rules are configured (UTC+3)
export const COMPANY_TIMEZONE = 'Europe/Istanbul';

/**
 * Get the current user's timezone
 * @returns {string} User's timezone identifier (e.g., 'America/New_York')
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get user's timezone offset in minutes from UTC
 * @returns {number} Offset in minutes (negative for ahead of UTC, positive for behind)
 */
export const getUserTimezoneOffset = () => {
  return new Date().getTimezoneOffset();
};

/**
 * Format timezone offset for display
 * @param {number} offsetMinutes - Timezone offset in minutes
 * @returns {string} Formatted timezone string (e.g., "GMT+3", "GMT-7")
 */
export const formatTimezoneOffset = (offsetMinutes) => {
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const hours = Math.abs(Math.floor(offsetMinutes / 60));
  const minutes = Math.abs(offsetMinutes % 60);
  
  if (minutes === 0) {
    return `GMT${sign}${hours}`;
  } else {
    return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  }
};

/**
 * Get user's timezone display string
 * @returns {string} User's timezone with current offset
 */
export const getUserTimezoneDisplay = () => {
  const offsetMinutes = getUserTimezoneOffset();
  return formatTimezoneOffset(offsetMinutes);
};

/**
 * Convert a company timezone hour to user's local timezone hour
 * @param {number} companyHour - Hour in company timezone (0-23)
 * @param {Date} referenceDate - Reference date for timezone calculation
 * @returns {Object} { hour, dayOffset } - Converted hour and day offset
 */
export const convertCompanyHourToUserHour = (companyHour, referenceDate = new Date()) => {
  try {
    // Create a date representing this hour in company timezone
    const companyDate = new Date(referenceDate);
    companyDate.setHours(companyHour, 0, 0, 0);
    
    // Get this time as it would be in company timezone
    const companyTimeString = companyDate.toLocaleString('sv-SE'); // YYYY-MM-DD HH:mm:ss format
    
    // Create a date object representing this exact time in company timezone
    const companyExactTime = new Date(companyTimeString + '+03:00'); // UTC+3 offset
    
    // This gives us the equivalent time in user's local timezone
    const userTime = new Date(companyExactTime);
    
    // Calculate day offset
    const dayOffset = userTime.getDate() - referenceDate.getDate();
    
    return {
      hour: userTime.getHours(),
      dayOffset: dayOffset
    };
  } catch (error) {
    console.error('Error converting company hour to user hour:', error);
    // Fallback: return the same hour
    return {
      hour: companyHour,
      dayOffset: 0
    };
  }
};

/**
 * Convert company timezone rules to user's local timezone
 * @param {Object} companyRules - Rules with timeSlots in company timezone
 * @param {Date} referenceDate - Reference date for conversion
 * @returns {Object} Rules converted to user's local timezone
 */
export const convertRulesToUserTimezone = (companyRules, referenceDate = new Date()) => {
  const userTimeSlots = {};
  
  // Initialize all hours as unavailable
  for (let hour = 0; hour < 24; hour++) {
    userTimeSlots[hour] = false;
  }
  
  // Convert each available hour from company timezone to user timezone
  if (companyRules.timeSlots) {
    for (let companyHour = 0; companyHour < 24; companyHour++) {
      const isAvailable = companyRules.timeSlots[companyHour] !== false;
      
      if (isAvailable) {
        const { hour: userHour } = convertCompanyHourToUserHour(companyHour, referenceDate);
        userTimeSlots[userHour] = true;
      }
    }
  }
  
  return {
    ...companyRules,
    timeSlots: userTimeSlots
  };
};

/**
 * Check if a date is available based on rules and labels
 * All data from Firestore is already in local timezone after automatic conversion
 * @param {Date} date - Date in user's timezone
 * @param {Object} rules - Rules object (timeSlots, weekDays, specialDates)
 * @param {Array} unavailableLabels - Array of unavailable day labels
 * @returns {boolean} Whether the date is available
 */
export const isDateAvailable = (date, rules, unavailableLabels) => {
  if (!date) return false;
  
  // Don't allow dates in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date < today) return false;
  
  // Check day-of-week rules (these need timezone conversion too)
  // Convert the user's date to company timezone to check day-of-week rules
  const companyDate = new Date(date.toLocaleString('en-US', {
    timeZone: COMPANY_TIMEZONE
  }));
  
  const dayOfWeek = companyDate.getDay(); // 0-6 (Sunday-Saturday)
  const isDayAvailableByRule = rules.weekDays ? rules.weekDays[dayOfWeek] !== false : true;
  
  // Check if it's a special date (using company timezone date)
  const isHoliday = rules.specialDates && rules.specialDates.some(specialDate => 
    specialDate.month === companyDate.getMonth() && specialDate.day === companyDate.getDate()
  );
  
  // Check if it has an unavailable label (labels are already in local timezone from Firestore)
  const hasUnavailable = unavailableLabels && unavailableLabels.some(label => {
    if (!label.label_date) return false;
    const labelDate = new Date(label.label_date);
    return (
      labelDate.getFullYear() === date.getFullYear() &&
      labelDate.getMonth() === date.getMonth() &&
      labelDate.getDate() === date.getDate()
    );
  });
  
  // Day is available if it's not a holiday, has no unavailable label, and is available by day of week rule
  return isDayAvailableByRule && !isHoliday && !hasUnavailable;
};

/**
 * Get available time slots for a specific day
 * Converts company timezone rules to user's local timezone
 * @param {Date} date - The date in user's timezone
 * @param {Object} rules - Rules object with timeSlots in company timezone
 * @returns {Array} Array of available hours in user's local timezone for the given date
 */
export const getAvailableTimeSlots = (date, rules) => {
  // Convert company rules to user timezone for this specific date
  const userRules = convertRulesToUserTimezone(rules, date);
  
  const availableHours = [];
  
  // Check each hour of the day in user's timezone
  for (let hour = 0; hour < 24; hour++) {
    // Check if this hour is available in converted rules
    const isAvailable = userRules.timeSlots[hour] === true;
    
    if (isAvailable) {
      availableHours.push(hour);
    }
  }
  
  return availableHours.sort((a, b) => a - b);
};

/**
 * Get booked time slots for a specific day
 * Bookings from Firestore are already converted to local timezone
 * @param {Date} date - The date in user's timezone
 * @param {Array} bookings - Array of booking objects with local label_date
 * @returns {Array} Array of booked hours for the given date
 */
export const getBookedTimeSlots = (date, bookings) => {
  const bookedHours = [];
  
  bookings.forEach(booking => {
    if (!booking.label_date || booking.whole_day) return;
    
    // Booking label_date is already converted to local timezone by Firestore
    const bookingTime = new Date(booking.label_date);
    
    // Check if this booking falls on the target date
    if (bookingTime.toDateString() === date.toDateString()) {
      bookedHours.push(bookingTime.getHours());
    }
  });
  
  return bookedHours;
};

/**
 * Convert date/time to specific timezone for display
 * @param {Date} date - Date to convert
 * @param {string} timezone - Target timezone
 * @returns {string} Formatted date/time string in target timezone
 */
export const formatDateInTimezone = (date, timezone) => {
  try {
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return date.toLocaleString();
  }
};